/**
 * Filesystem abstraction for guest operations.
 *
 * Implements the file and directory operations required by WASIX syscalls:
 * open, close, read, write, truncate, stat, timestamp updates, rename, unlink,
 * mkdir, rmdir, readlink, and readdir.
 *
 * File descriptor table management, offsets, close-on-exec, and descriptor
 * duplication are handled by the runtime layer above. Handles identify open files
 * across processes attached to the same VFS.
 */

/** Constant values from the witx, via wasmer's wasi-types crate. */
export const Filetype = {
    UNKNOWN: 0,
    BLOCK_DEVICE: 1,
    CHARACTER_DEVICE: 2,
    DIRECTORY: 3,
    REGULAR_FILE: 4,
    SYMBOLIC_LINK: 7,
} as const;

export type Filetype = (typeof Filetype)[keyof typeof Filetype];

/** Opaque handle identifying an open file across processes attached to the same VFS. */
export type Handle = number;

/** What a stat call answers, in the fields `filestat_get` writes out. */
export interface Stat {
    filetype: Filetype;
    dev: number;
    ino: number;
    nlink: number;
    size: number;
    atimeMs: number;
    mtimeMs: number;
    ctimeMs: number;
}

/** Directory entry returned by readdir. */
export interface DirEntry {
    name: string;
    ino: number;
    filetype: Filetype;
}

export interface OpenOptions {
    read: boolean;
    write: boolean;
    /** Create the file if it is not there. */
    create: boolean;
    /** With `create`, fail if it is. */
    exclusive: boolean;
    /** Discard whatever is in it. */
    truncate: boolean;
    /** Appends all writes to the end of the file. */
    append: boolean;
}

/**
 * Serializable snapshot of a filesystem and its shared memory buffers,
 * passed to workers via postMessage to attach to the same VFS.
 */
export type VfsSnapshot = NodeVfsSnapshot | MemVfsSnapshot;

export interface NodeVfsSnapshot {
    kind: "node";
    mounts: Mount[];
    /** The table of open files — see NodeVfs. */
    open: SharedArrayBuffer;
}

export interface MemVfsSnapshot {
    kind: "mem";
    arena: SharedArrayBuffer;
}

/** A guest directory, and the host directory whose contents it is. */
export interface Mount {
    guestPath: string;
    hostPath: string;
}

export interface Vfs {
    /** Unique identifier for the filesystem instance, used for module caching. */
    readonly id: string;

    /** Opens a file and returns a handle with an initial reference count. */
    open(path: string, opts: OpenOptions): Handle;

    /** Increments the reference count for an open file handle. */
    retain(handle: Handle): void;

    /** Decrements the reference count for an open file handle, closing it when zero. */
    close(handle: Handle): void;

    /** Releases thread-local resources held by the current process. */
    processEnded(): void;

    /** Reads up to into.length bytes at the specified file offset. */
    read(handle: Handle, into: Uint8Array, position: number): number;

    /** Writes bytes at the specified offset, or at the end if position is null. */
    write(handle: Handle, from: Uint8Array, position: number | null): number;

    truncate(handle: Handle, size: number): void;

    stat(path: string, follow: boolean): Stat;
    statHandle(handle: Handle): Stat;

    setTimes(path: string, atimeMs: number, mtimeMs: number, follow: boolean): void;
    setTimesHandle(handle: Handle, atimeMs: number, mtimeMs: number): void;

    rename(from: string, to: string): void;
    unlink(path: string): void;
    mkdir(path: string): void;
    rmdir(path: string): void;
    readlink(path: string): string;
    readdir(path: string): DirEntry[];

    snapshot(): VfsSnapshot;
}

/** Read-only, for the small files above this layer that want one call. */
export const READ: OpenOptions = {
    read: true,
    write: false,
    create: false,
    exclusive: false,
    truncate: false,
    append: false,
};
